import React, { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, IconButton, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Switch } from '@repo/ui'
import { ArrowLeft, Copy, Eye, MoreVertical, Plus, Settings, Share2, Trash2 } from 'lucide-react'
import { CertificateViewAccess } from '@repo/types'
import type { TCertificateViewVisibility } from '@repo/types'

import { ActionMenu } from 'components/UI/ActionMenu'
import type { ActionMenuItem } from 'components/UI/ActionMenu'
import { useSnackbar } from 'components/Snackbar'
import { PageContainer } from 'components/UI/PageContainer'
import { SectionLabel } from 'components/UI/SectionLabel'
import { TermInfo } from 'components/UI/TermInfo'
import { PAGE_GUTTER, PAGE_GUTTER_MOBILE } from 'constants/layout'
import {
  CertificateView,
  useCertificateViews,
  useCreateCertificateView,
  useInviteViewersToView,
  useDeleteCertificateView,
  useUpdateCertificateView,
} from 'hooks/api/useCertificateViews'

import { usePushPage } from 'hooks/usePushPage'
import {
  ActionBarPortal,
  ActionBarProvider,
  ActionBarSpacer,
  StickyActionBar,
} from 'components/UI/StickyActionBar'
import { useScrolledPast } from 'hooks/useScrolledPast'
import { glassStyle, glassUnsupportedSx } from 'utils/glass'
import { accessSummary, visibilitySummary } from './viewSummary'
import { DeleteViewModal } from './components/DeleteViewModal'
import { DisableViewModal } from './components/DisableViewModal'
import { ViewSettingsDrawer } from './components/ViewSettingsDrawer'

const viewLabel = (view: CertificateView) =>
  view.kind === 'public' ? 'Public view' : view.name || 'Custom view'

/**
 * VERA-299 / VERA-298 — the Share screen.
 *
 * Lists the certificate's shareable views. Today that is the public singleton;
 * custom ("dynamic") links join the same list under VERA-300, which is why the row
 * and the settings drawer are written against `kind` rather than assuming one view.
 *
 * Owner-only: the views endpoint 404s for anyone else, and the route sits behind
 * ProtectedRoute.
 */
export const SharePage = () => {
  const theme = useTheme()
  /* `md` is 720 in this app, NOT MUI's 900 — see settings/mui/breakpoints.ts.
     It is the same breakpoint the header row already uses to swap its gutter
     between PAGE_GUTTER_MOBILE and PAGE_GUTTER, so the action moves into the
     header on exactly the width where the header becomes a desktop one. */
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { certificateId } = useParams()
  const { openSnackbar } = useSnackbar()

  const { data: views, isLoading } = useCertificateViews(certificateId)
  const { mutate: updateView, isPending } = useUpdateCertificateView(certificateId as string)
  const { mutate: createView, isPending: isCreatingView } = useCreateCertificateView(
    certificateId as string
  )
  const { mutate: inviteToView } = useInviteViewersToView(certificateId as string)
  const { mutate: deleteView, isPending: isDeleting } = useDeleteCertificateView(
    certificateId as string
  )

  const [optionsFor, setOptionsFor] = useState<CertificateView | null>(null)
  const [settingsFor, setSettingsFor] = useState<CertificateView | null>(null)
  const [disableTarget, setDisableTarget] = useState<CertificateView | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CertificateView | null>(null)
  // VERA-300 — the create form. A custom view is no longer written to the DB
  // until Save; this just toggles the drawer that collects its name + privacy
  // settings first.
  const [isCreating, setIsCreating] = useState(false)

  /*
   * The shared push transition: enters from the RIGHT, leaves back out to the
   * right, and holds the page mounted for the exit before routing (see
   * `usePushPage` — `navigate()` unmounts on the same tick, so an animation
   * started at click time has nothing left to animate).
   *
   * `pushOnly` because this route has no cold entry to protect: the app runs on
   * a `MemoryRouter`, so `/certificates/:id/share` has no URL to type or scan
   * and is only ever reached from a screen already on the stack. Its callers
   * therefore do not need to send `pushState()` — which matters, since one of
   * them is the certificate page's floating share disc.
   */
  const { pageSx, goBack } = usePushPage(`/certificates/${certificateId}`, {
    pushOnly: true,
  })

  const headerSentinelRef = useRef<HTMLDivElement | null>(null)
  const isHeaderStuck = useScrolledPast(headerSentinelRef, 0)

  const publicView = useMemo(() => views?.find((v) => v.kind === 'public') ?? null, [views])
  const customViews = useMemo(() => views?.filter((v) => v.kind === 'custom') ?? [], [views])

  const copyLink = (view: CertificateView) => {
    navigator.clipboard.writeText(view.url)
    openSnackbar({ severity: 'success', title: `${viewLabel(view)} link copied to clipboard` })
  }

  /**
   * VERA-298 — send the link through the OS share sheet (Messages, Mail, AirDrop),
   * falling back to the clipboard where `navigator.share` does not exist (desktop).
   *
   * This lives in the row's kebab rather than inline: copying is the more frequent
   * action and holds the inline slot, but sending is the capability that would
   * otherwise have been lost when the old share drawer was deleted, and copy-then-paste
   * is a strictly worse way to get a link into a message on a phone.
   */
  const shareView = async (view: CertificateView) => {
    try {
      if ((navigator as any)?.share) {
        await (navigator as any).share({ url: view.url })
      } else {
        await navigator.clipboard.writeText(view.url)
        openSnackbar({ severity: 'success', title: `${viewLabel(view)} link copied to clipboard` })
      }
    } catch {
      // User dismissed the native share sheet — not an error worth surfacing.
    }
  }

  const toggleView = (view: CertificateView, checked: boolean) => {
    // Turning a link OFF revokes access for everyone holding it, so it is confirmed.
    // Turning it back ON is not destructive and applies straight away.
    if (!checked) {
      setDisableTarget(view)
      return
    }

    updateView({ viewId: view.id, patch: { is_enabled: true } })
  }

  const saveViewSettings = (
    view: CertificateView,
    values: { name: string; visibility: TCertificateViewVisibility }
  ) => {
    updateView(
      {
        viewId: view.id,
        // The public view has no user-editable name (its Name field is hidden),
        // so its patch never touches `name` regardless of what the drawer's
        // internal state happens to hold.
        patch:
          view.kind === 'public'
            ? { visibility: values.visibility }
            : { name: values.name, visibility: values.visibility },
      },
      {
        onSuccess: () => {
          setSettingsFor(null)
          openSnackbar({ severity: 'success', title: 'View settings updated' })
        },
      }
    )
  }

  /**
   * Duplicate copies the VISIBILITY, not the link. The new view gets its own slug, so
   * the original's recipients are unaffected — which is the point of duplicating
   * rather than re-sharing.
   */
  const duplicateView = (view: CertificateView) => {
    createView(
      { name: `${viewLabel(view)} copy`, visibility: view.visibility },
      {
        onSuccess: created =>
          openSnackbar({ severity: 'success', title: `${created.name} created` }),
      }
    )
  }

  const renderRow = (view: CertificateView) => (
    <Stack
      key={view.id}
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      spacing={1}
      sx={{ py: 2 }}
    >
      {/* The row body taps through to Settings — the standard list->detail gesture, so
          the most common destination costs one tap instead of kebab + pick. */}
      <Box
        component='button'
        onClick={() => setSettingsFor(view)}
        aria-label={`Settings for ${viewLabel(view)}`}
        sx={{
          minWidth: 0,
          flex: 1,
          border: 'none',
          background: 'none',
          font: 'inherit',
          textAlign: 'left',
          p: 0,
          cursor: 'pointer',
          color: 'inherit',
        }}
      >
        <Typography sx={{ fontSize: 16, lineHeight: '20px' }}>{viewLabel(view)}</Typography>
        {/*
          Two facts, one subtitle: who can open it, and what it withholds. The
          separator is a middot rather than a second line because they are the
          same KIND of fact about the same link, and stacking them made the row
          read as two rows.

          It WRAPS. The old `nowrap` + ellipsis was inherited from when this line
          held a URL, where truncating mid-slug cost nothing. Truncating a privacy
          disclosure to "…" is the one thing this line must never do, and at 375px
          an invited view with three modules hidden does not fit on one line.
        */}
        <Typography
          variant='body2'
          sx={{ color: theme.palette.text.secondary, lineHeight: 1.4 }}
        >
          {accessSummary(view)} &middot; {visibilitySummary(view)}
        </Typography>
      </Box>

      <Stack direction='row' alignItems='center' spacing={1}>
        {/* Share is the row's whole purpose, so it is inline rather than one tap deep.
            Preview / Duplicate / Delete stay in the kebab: putting them inline too
            would cost ~100px and truncate the slug, which is the only part of the URL
            that distinguishes one link from another. */}
        <IconButton
          size='small'
          aria-label={`Copy link for ${viewLabel(view)}`}
          sx={{ color: theme.palette.text.primary }}
          onClick={() => copyLink(view)}
        >
          <Copy size={18} />
        </IconButton>

        {/*
          The PUBLIC view has no enable toggle and cannot be deleted: it is the
          destination a scanned Vera Tag resolves to, so switching it off would leave
          the physical tag pointing at nothing. Privacy is controlled by the module
          toggles in Settings instead — the certificate stays reachable, the sensitive
          sections do not. Only custom links, which are additive and disposable, can
          be turned off.

          NOTE: this deliberately diverges from Figma `3054:983710`, whose confirm
          modal reads "disable Public view".
        */}
        {view.kind === 'custom' && (
          // Toggling IS the feedback here, so no success toast on the happy path.
          <Switch
            checked={view.is_enabled}
            disabled={isPending}
            onChange={(_, checked) => toggleView(view, checked)}
          />
        )}
        <IconButton
          size='small'
          aria-label={`Options for ${viewLabel(view)}`}
          sx={{ color: theme.palette.text.primary }}
          onClick={() => setOptionsFor(view)}
        >
          <MoreVertical size={20} />
        </IconButton>
      </Stack>
    </Stack>
  )

  return (
    // The layout's <main> paints nothing, so each page root supplies its own surface
    // (same as CertificatePage). Without this the page is transparent and its black
    // text lands on the browser's default backdrop.
    <ActionBarProvider>
    <Stack
      sx={{
        /* SLIDES in from the right and back out to the right, like every other
           pushed screen. This used to RISE, on the argument that Share is a task
           you open and dismiss. But the rise is the bottom-SHEET gesture, and
           this screen is a full page — with real bottom sheets (the options menu,
           the settings drawer) one tap away on it, so borrowing their motion
           promised a sheet and delivered a page. */
        ...pageSx,
        background: theme.palette.background.default,
        minHeight: '100vh',
        // NO `px` — it moved to the `PageContainer` below. The BACKGROUND stays on
        // this full-width element deliberately: painted on the 1120 column instead,
        // it would stop at the column's edge and expose the backdrop down both
        // sides of a desktop window.
        pb: 4,
      }}
    >
      <PageContainer sx={{ flex: '1 1 auto' }}>
      {/* Sentinel BEFORE the pinned row, never inside it — inside, it travels
          with the bar and the stuck flag can never flip. */}
      <Box ref={headerSentinelRef} aria-hidden sx={{ height: '1px' }} />
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        /* Background and border set inline: `#vera-shadow-root *` in index.css
           wins on ID specificity over an sx border colour, and emotion caches the
           class across the stuck flip. */
        style={{
          /* The shared token's inline form. The fill was a LITERAL
             `rgba(255,255,255,0.82)`, which is `alpha(background.paper, 0.82)`
             spelled out — the same pixels today, but a hardcoded white that
             would not have followed the token if the material ever changed. */
          ...glassStyle(theme, isHeaderStuck),
          borderBottomColor: isHeaderStuck ? theme.palette.grey[200] : 'transparent',
        }}
        sx={{
          py: 1,
          // Pinned, with the app's one glass — parity with the certificate
          // sub-views, the certificates list header and the bottom nav.
          position: 'sticky',
          top: 0,
          zIndex: 3,
          // Full-bleed to the COLUMN's edge while pinned, so rows do not slide past
          // in the gutters. Responsive because the gutter it cancels is
          // `PageContainer`'s, which is 16 at `xs` and `PAGE_GUTTER` at `md` — a
          // flat `-2` left an 8px sliver of unglassed page down each side at
          // desktop width.
          mx: { xs: `-${PAGE_GUTTER_MOBILE}px`, md: `-${PAGE_GUTTER}px` },
          px: { xs: `${PAGE_GUTTER_MOBILE}px`, md: `${PAGE_GUTTER}px` },
          borderBottom: '1px solid transparent',
          transition: 'background-color .15s ease, border-color .15s ease',
          /* NEW, not a refactor: this header had no `@supports` fallback either,
             so without backdrop-filter it pinned at 82% and the list showed
             through. */
          ...glassUnsupportedSx(theme, isHeaderStuck),
        }}
      >
        <IconButton
          size='small'
          aria-label='Back'
          sx={{ color: theme.palette.text.primary, ml: -1 }}
          onClick={goBack}
        >
          <ArrowLeft size={24} />
        </IconButton>
        {/* `flexGrow: 1` is what right-aligns the action beside it, and it has to
            live HERE rather than as `ml: 'auto'` on the button.

            `Stack spacing={1}` emits `.MuiStack-root > :not(style) ~ :not(style)
            { margin-left: 8px }`, which out-specifies a child's own `sx` — so
            `ml: 'auto'` on the button computed to `8px` and it rendered hard
            against the title (measured: left 371, in a 1040-wide row ending at
            1280). Growing the TITLE takes the slack instead, which nothing
            overrides. The text stays left-aligned inside its own wider box, and
            on mobile there is no sibling after it, so this is inert there. */}
        <Typography sx={{ fontSize: 20, flexGrow: 1 }}>Share</Typography>
        {/*
          At pointer width the primary moves UP HERE, inline with the title and
          hard against the column's right edge — working-conventions §26/§33, one
          primary, rightmost.

          The sticky bar below is right on a phone and wrong here, for the same
          reason each is right: it pins to the BOTTOM OF THE VIEWPORT. On a phone
          that is thumb reach with the list scrolling under it. On a 1280 desktop
          the list is three rows tall and the viewport is 860, so the bar floats
          against ~600px of empty page, anchored to nothing and half a screen away
          from the content it acts on.

          It stays in the pinned header rather than moving into page flow, so the
          original rationale still holds: a certificate can accumulate enough views
          to scroll a flow-positioned action out of sight. Pinned at the top, it
          survives a long list exactly as the bottom bar did.
        */}
        {isDesktop && (
          <Box>
            <Button
              variant='contained'
              startIcon={<Plus size={18} />}
              onClick={() => setIsCreating(true)}
              sx={{ textTransform: 'none' }}
            >
              Add custom view
            </Button>
          </Box>
        )}
      </Stack>

      {isLoading ? (
        // Skeleton mirrors the row it replaces (label line + url line + control) so
        // there is no layout shift when the views land.
        <Stack sx={{ py: 2 }} spacing={1}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant='text' width='40%' height={20} />
              <Skeleton variant='text' width='75%' height={18} />
            </Box>
            <Skeleton variant='rounded' width={44} height={24} />
          </Stack>
        </Stack>
      ) : (
        <>
          {/* The public-view disclosure that used to render as a paragraph here is
              now the row's own subtitle (`visibilitySummary`), which is what finally
              attaches it to a view — and gives every custom link the same disclosure
              instead of only the public one. */}
          {publicView && (
            <>
              {/*
                No `first`: there IS a page header above this, so the caption
                wants the same 32px it gets between sections rather than butting
                up under "Share". `first` is for a caption that opens its
                container with nothing above it.

                SectionLabel, not a bold `subtitle1`. This and "Custom views"
                are a sibling PAIR partitioning the page's list, which is the
                same job GENERAL / LOG IN do on Profile and DETAILS / HISTORY /
                MANAGE do on the certificate — so they take the same caption and
                the same 32px rhythm rather than a third heading style.

                (`subtitle1` is fontWeight 600, and the theme's own FONT_WEIGHTS
                note explains that a missing 600 snaps UP to 700 — so it was not
                merely semibold here, it was full bold.)
              */}
              {/*
                VRG-426 — the page's ONE `(i)`, on the FIRST caption that says "view".

                "View" is the word this whole screen is built out of and the only one
                on it that is product vocabulary rather than English: it is not a
                rendering of the certificate, it is a shareable link carrying its own
                audience and its own disclosure. Nothing on the page says that — the
                rows carry a name and a visibility summary, both of which presuppose it.

                Here rather than on "Custom views" because this caption renders whenever
                a public view exists, i.e. always, whereas the sibling only appears once
                the reader has already created one — by which point they have met the
                concept. One anchor, on the first mention.

                A row wrapping `SectionLabel inline`, which is the prop's stated purpose
                ("sitting on a shared row … drops both margins so the row's own alignment
                controls the spacing"). The 32/8 rhythm moves to the Stack unchanged;
                dropping the `(i)` INSIDE the caption would put a 22px control in a 14px
                uppercase line box and inflate the gap it is supposed to preserve.
              */}
              <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mt: 4, mb: 1 }}>
                <SectionLabel inline>Default view</SectionLabel>
                <TermInfo termId='certificate-view' />
              </Stack>
              {renderRow(publicView)}
            </>
          )}

          {customViews.length > 0 && (
            <>
              <SectionLabel>Custom views</SectionLabel>
              {customViews.map(renderRow)}
            </>
          )}
        </>
      )}

      {/* Primary: creating a link is this screen's one main action, and everything else
          here is a per-row control. Opens the create-mode ViewSettingsDrawer rather
          than writing a view immediately (VERA-300).

          Sticky so it survives a list longer than the viewport: `mt: 'auto'` alone
          only pins it while the content is short, and a certificate can accumulate
          enough views to scroll the action off the bottom. Same treatment as the
          scans/provenance sort drawers (ScansSortDrawer.tsx:140) rather than a new
          one. `mt: 'auto'` stays so it still sits at the bottom when the list is
          short instead of riding up under the last row. */}
      {/*
        The SHARED action bar, not a second hand-rolled one.

        This was a sticky Box painting opaque #ffffff with its own grey border —
        a second implementation of the pinned-action pattern, and visibly not the
        same surface as the certificate page's bar one screen away. Routing it
        through ActionBarPortal buys the glass, the rise-in animation, the
        safe-area padding and the flow spacer from one place (working-conventions
        §36), and removes the divergence rather than patching its colour.
      */}
      {/* Phone only — at `md` and up this same action renders in the header row
          above. Both halves are gated on the SAME `isDesktop`, so the primary
          appears exactly once at every width; rendering both and hiding one with
          `display` would leave two live buttons in the a11y tree. */}
      {!isDesktop && (
        <>
          <ActionBarSpacer />
          <ActionBarPortal>
            <Button
              fullWidth
              variant='contained'
              startIcon={<Plus size={18} />}
              onClick={() => setIsCreating(true)}
              sx={{ textTransform: 'none', py: 1.25 }}
            >
              Add custom view
            </Button>
          </ActionBarPortal>
        </>
      )}

      {/* The shared ActionMenu (VERA-315), not a hand-rolled drawer — so this menu
          inherits the app's row styling, per-item icons and destructive treatment from
          one place instead of drifting. Preview leads: seeing what a link exposes is
          the thing an owner reaches for most, and it reads before the settings that
          change it. */}
      <ActionMenu
        open={Boolean(optionsFor)}
        onClose={() => setOptionsFor(null)}
        items={
          [
            {
              label: 'Share',
              icon: <Share2 size={18} />,
              onClick: () => {
                if (optionsFor) shareView(optionsFor)
              },
            },
            {
              label: 'Preview',
              icon: <Eye size={18} />,
              onClick: () => {
                if (optionsFor) {
                  navigate(`/certificates/${certificateId}?previewView=${optionsFor.slug}`)
                }
              },
            },
            {
              label: 'Settings',
              icon: <Settings size={18} />,
              onClick: () => setSettingsFor(optionsFor),
            },
            {
              label: 'Duplicate',
              icon: <Copy size={18} />,
              onClick: () => optionsFor && duplicateView(optionsFor),
              visible: optionsFor?.kind === 'custom',
            },
            {
              label: 'Delete',
              icon: <Trash2 size={18} />,
              destructive: true,
              onClick: () => setDeleteTarget(optionsFor),
              visible: optionsFor?.kind === 'custom',
            },
          ] as ActionMenuItem[]
        }
      />

      <ViewSettingsDrawer
        certificateId={certificateId as string}
        view={settingsFor}
        open={Boolean(settingsFor)}
        isSaving={isPending}
        onClose={() => setSettingsFor(null)}
        onAccessChange={(access) => {
          if (!settingsFor) return
          // Immediate, like the invite/remove actions beside it. Optimism would be
          // wrong here: this widens or narrows who can read the certificate, so the
          // UI should not claim it changed until the server says so.
          updateView(
            { viewId: settingsFor.id, patch: { access } },
            {
              /*
                No toast. The card the user just clicked visibly becomes the
                selected one, and the sentence under it already says exactly what
                the toast said — so the toast restated, on top of the control, a
                fact the control was displaying. Toasts are for outcomes with no
                visible surface (a copied link, an email that failed to send).
              */
              onSuccess: setSettingsFor,
            }
          )
        }}
        onSave={(values) => settingsFor && saveViewSettings(settingsFor, values)}
      />

      {/* VERA-300 — a separate drawer instance in `create` mode rather than
          reusing `settingsFor` with a fabricated view: nothing is written
          until Create, so there is no view object to hold yet. */}
      <ViewSettingsDrawer
        certificateId={certificateId as string}
        view={null}
        mode='create'
        open={isCreating}
        isSaving={isCreatingView}
        onClose={() => setIsCreating(false)}
        onSave={({ name, visibility, inviteEmails }) =>
          createView(
            { name, visibility },
            {
              onSuccess: (created) => {
                setIsCreating(false)

                // Invites are collected in the create form but can only be sent once
                // the view has an id, so they are posted here rather than in the
                // drawer. Creation is already committed at this point, so a failed
                // invite must not read as a failed create: the two outcomes are
                // reported separately.
                if (inviteEmails.length === 0) {
                  openSnackbar({ severity: 'success', title: `${created.name} created` })
                  return
                }

                inviteToView(
                  { viewId: created.id, emails: inviteEmails },
                  {
                    onSuccess: (result) => {
                      const failed = result.email_failures.length
                      openSnackbar(
                        failed > 0
                          ? {
                              severity: 'warning',
                              title: `${created.name} created. ${failed} invite email${failed === 1 ? '' : 's'} could not be sent`,
                            }
                          : {
                              severity: 'success',
                              title: `${created.name} created and ${result.invited.length} invited`,
                            }
                      )
                    },
                    onError: () =>
                      openSnackbar({
                        severity: 'warning',
                        title: `${created.name} created, but the invites could not be sent`,
                      }),
                  }
                )
              },
            }
          )
        }
      />

      <DeleteViewModal
        open={Boolean(deleteTarget)}
        viewLabel={deleteTarget ? viewLabel(deleteTarget) : ''}
        isSaving={isDeleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return

          deleteView(deleteTarget.id, {
            onSuccess: () => {
              const label = viewLabel(deleteTarget)
              setDeleteTarget(null)
              openSnackbar({ severity: 'success', title: `${label} deleted` })
            },
          })
        }}
      />

      <DisableViewModal
        open={Boolean(disableTarget)}
        viewLabel={disableTarget ? viewLabel(disableTarget) : ''}
        isSaving={isPending}
        onCancel={() => setDisableTarget(null)}
        onConfirm={() => {
          if (!disableTarget) return

          updateView(
            { viewId: disableTarget.id, patch: { is_enabled: false } },
            {
              onSuccess: () => {
                setDisableTarget(null)
                openSnackbar({ severity: 'success', title: 'Public view has been disabled' })
              },
            }
          )
        }}
      />
      </PageContainer>
    </Stack>
    {/* Mounted OUTSIDE the page Stack: the bar is `position: fixed`, and the page
        root carries the slide transform — a transformed ancestor becomes the
        containing block for a fixed child, which is the same trap that made the
        certificate header sticky rather than fixed. (The direction changed from
        a rise to a horizontal slide; the constraint is identical either way, so
        the bar stays out here.) */}
    <StickyActionBar />
    </ActionBarProvider>
  )
}
